import * as React from "react";
import {
  makeStyles,
  FluentProvider,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  Text,
  Label,
  Toast,
  ToastTitle,
  ToastBody,
  ToastFooter,
  Link,
  useToastController,
  Toaster,
} from "@fluentui/react-components";
import { ArrowClockwise24Regular } from "@fluentui/react-icons";
import { agrelloTheme } from "../theme";
import { getDocumentAsFile, getDocumentName } from "../word-api";
import { login, logout } from "../auth/auth";
import { getCredentials, saveCredentials, clearCredentials } from "../auth/authHelper";
import { getWorkspace, getFolders, createContainerInFolder, setDebugInfoUpdater } from "../auth/agrello-api";
import StatusBar, { ConnectionStatus } from "./StatusBar";
import CredentialsForm from "./CredentialsForm";
import SignersList from "./SignersList";
import SigningOptions from "./SigningOptions";
import Footer from "./Footer";
import DebugDialog from "./DebugDialog";

const AGRELLO_DOCS_BASE_URL = "https://docs.agrello.io";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: "#f9fafb",
    padding: "16px",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  section: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    padding: "16px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: "12px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    marginBottom: "12px",
  },
  folderRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "8px",
    marginTop: "8px",
  },
  content: {
    flexGrow: 1,
    overflow: "auto",
    minHeight: 0,
  },
});

const App: React.FC = () => {
  const styles = useStyles();
  const [connectionStatus, setConnectionStatus] = React.useState<ConnectionStatus>("loading");
  const [connectionError, setConnectionError] = React.useState<string>("");
  const [debugInfo, setDebugInfo] = React.useState("");
  const [workspace, setWorkspace] = React.useState<any>(null);
  const [folders, setFolders] = React.useState<any[]>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<string | null>(null);
  const [selectedFolderName, setSelectedFolderName] = React.useState<string>("");
  const [isCreatingContainer, setIsCreatingContainer] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [signers, setSigners] = React.useState<string[]>([""]);
  const [documentName, setDocumentName] = React.useState<string>("");
  const [outputFormat, setOutputFormat] = React.useState<string>("PDF");
  const [sendImmediately, setSendImmediately] = React.useState(true);
  const [isDebugDialogOpen, setIsDebugDialogOpen] = React.useState(false);
  const [showDebugButton, setShowDebugButton] = React.useState(false);
  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);

  const { dispatchToast, dismissToast } = useToastController();

  const updateDebugInfo = (message: string) => {
    setDebugInfo(function (prevInfo) {
      return prevInfo + "\n" + message;
    });
  };

  // Initialize on mount
  React.useEffect(() => {
    setDebugInfoUpdater(updateDebugInfo);
    checkCredentials();

    var isDevelopment = process.env.NODE_ENV === "development";
    var debugModeLocalStorage = window.localStorage.getItem("agrelloDebugMode") === "true";
    setShowDebugButton(isDevelopment || debugModeLocalStorage);

    if (isDevelopment) {
      setDebugInfo("Debug mode auto-enabled (development environment)");
    }
  }, []);

  // Load saved folder
  React.useEffect(() => {
    var savedFolder = localStorage.getItem("selectedFolder");
    if (savedFolder) {
      setSelectedFolder(savedFolder);
    }
  }, []);

  // Fetch document name when connected
  React.useEffect(() => {
    if (connectionStatus === "connected") {
      fetchDocumentName();
    }
  }, [connectionStatus]);

  const checkCredentials = async () => {
    setConnectionStatus("loading");
    try {
      var credentials = await getCredentials();
      if (credentials) {
        await handleConnect(credentials.clientId, credentials.clientSecret);
      } else {
        setConnectionStatus("disconnected");
      }
    } catch (error) {
      setConnectionStatus("disconnected");
    }
  };

  const fetchWorkspaceAndFolders = async () => {
    setIsFoldersLoading(true);
    try {
      var workspaceData = await getWorkspace();
      setWorkspace(workspaceData);
      updateDebugInfo("Workspace fetched successfully");

      var foldersData = await getFolders();
      setFolders(foldersData);
      updateDebugInfo("Folders fetched successfully");
    } catch (error) {
      updateDebugInfo("Failed to fetch workspace or folders: " + (error.message || JSON.stringify(error)));
    } finally {
      setIsFoldersLoading(false);
    }
  };

  const handleConnect = async (clientId: string, clientSecret: string) => {
    setIsConnecting(true);
    setConnectionError("");
    updateDebugInfo("Connecting...");
    try {
      await saveCredentials(clientId, clientSecret);
      var result = await login();
      setConnectionStatus("connected");
      updateDebugInfo("Login successful. Result: " + JSON.stringify(result));
      await fetchWorkspaceAndFolders();
    } catch (error) {
      var msg = error.message || JSON.stringify(error);
      setConnectionStatus("error");
      setConnectionError("Login failed: " + msg);
      updateDebugInfo("Login failed: " + msg);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await logout();
      await clearCredentials();
      setConnectionStatus("disconnected");
      setWorkspace(null);
      setFolders([]);
      setSelectedFolder(null);
      setSelectedFolderName("");
      updateDebugInfo("Disconnected successfully");
    } catch (error) {
      updateDebugInfo("Disconnect failed: " + (error.message || JSON.stringify(error)));
    }
  };

  const createContainer = async () => {
    if (!selectedFolder) {
      dispatchToast(
        <Toast>
          <ToastTitle>Please select a folder first</ToastTitle>
        </Toast>,
        { intent: "error" }
      );
      return;
    }

    var signersList = signers
      .map(function (s) {
        return s.trim();
      })
      .filter(function (s) {
        return s !== "";
      });

    if (signersList.length === 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>Please add at least one signer</ToastTitle>
        </Toast>,
        { intent: "error" }
      );
      return;
    }

    setIsCreatingContainer(true);
    try {
      var file = await getDocumentAsFile();

      var containerData = {
        name: documentName || "Document from Word Add-in",
        outputType: outputFormat,
        variables: [],
        signers: signersList,
        immediatePublish: sendImmediately,
        metadata: {
          additionalProp1: "string",
          additionalProp2: "string",
          additionalProp3: "string",
        },
      };

      var newContainer = await createContainerInFolder(selectedFolder, containerData, file);
      updateDebugInfo("Container created successfully: " + JSON.stringify(newContainer));

      if (!newContainer || !newContainer.id || !newContainer.name) {
        throw new Error("Container response missing required fields (id or name)");
      }

      var containerUrl = AGRELLO_DOCS_BASE_URL + "/app/containers/" + newContainer.id;
      var toastId = "toast-" + Date.now() + "-" + Math.random();
      dispatchToast(
        <Toast>
          <ToastTitle>Document sent for signing!</ToastTitle>
          <ToastBody>
            <div>
              <strong>{newContainer.name}</strong>
            </div>
            <Link href={containerUrl} target="_blank">
              Open in Agrello
            </Link>
          </ToastBody>
          <ToastFooter>
            <Link onClick={() => dismissToast(toastId)}>Dismiss</Link>
          </ToastFooter>
        </Toast>,
        { toastId: toastId, intent: "success", timeout: -1 }
      );
    } catch (error) {
      updateDebugInfo("Failed to create container: " + (error.message || JSON.stringify(error)));
      dispatchToast(
        <Toast>
          <ToastTitle>Failed to create container</ToastTitle>
        </Toast>,
        { intent: "error" }
      );
    } finally {
      setIsCreatingContainer(false);
    }
  };

  const handleFolderSelect = (folderId: string) => {
    setSelectedFolder(folderId);
    localStorage.setItem("selectedFolder", folderId);
    var folder = folders.find(function (f) {
      return f.id === folderId;
    });
    if (folder) {
      setSelectedFolderName(folder.name);
    }
  };

  const refreshFolders = async () => {
    setIsFoldersLoading(true);
    try {
      var foldersData = await getFolders();
      setFolders(foldersData);
      updateDebugInfo("Folders refreshed successfully");
      dispatchToast(
        <Toast>
          <ToastTitle>Folders refreshed</ToastTitle>
        </Toast>,
        { intent: "success" }
      );
    } catch (error) {
      updateDebugInfo("Failed to refresh folders: " + (error.message || JSON.stringify(error)));
      dispatchToast(
        <Toast>
          <ToastTitle>Failed to refresh folders</ToastTitle>
        </Toast>,
        { intent: "error" }
      );
    } finally {
      setIsFoldersLoading(false);
    }
  };

  const fetchDocumentName = async () => {
    try {
      var name = await getDocumentName();
      setDocumentName(name);
    } catch (error) {
      updateDebugInfo("Failed to fetch document name: " + (error.message || JSON.stringify(error)));
    }
  };

  const copyDebugInfo = () => {
    navigator.clipboard
      .writeText(debugInfo)
      .then(function () {
        dispatchToast(
          <Toast>
            <ToastTitle>Debug info copied to clipboard</ToastTitle>
          </Toast>,
          { intent: "success" }
        );
      })
      .catch(function (err) {
        console.error("Failed to copy debug info: ", err);
      });
  };

  var hasValidSigners = signers.some(function (s) {
    return s.trim() !== "";
  });

  return (
    <FluentProvider theme={agrelloTheme}>
      <div className={styles.root}>
        <div className={styles.content}>
          <StatusBar
            status={connectionStatus}
            workspaceName={workspace ? workspace.name : undefined}
            errorMessage={connectionError}
            onDisconnect={handleDisconnect}
          />

          {connectionStatus === "disconnected" || connectionStatus === "error" ? (
            <CredentialsForm onConnect={handleConnect} isConnecting={isConnecting} />
          ) : null}

          {connectionStatus === "connected" && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle} block>
                Send for signing
              </Text>

              <div className={styles.fieldGroup}>
                <Label htmlFor="documentNameInput" size="small">
                  Document name
                </Label>
                <Input
                  id="documentNameInput"
                  value={documentName}
                  onChange={(_, data) => setDocumentName(data.value)}
                />
              </div>

              <div className={styles.fieldGroup}>
                <Label htmlFor="folderDropdown" size="small">
                  Folder
                </Label>
                <div className={styles.folderRow}>
                  <Dropdown
                    id="folderDropdown"
                    placeholder={isFoldersLoading ? "Loading folders..." : "Select a folder"}
                    value={selectedFolder ? selectedFolderName : undefined}
                    onOptionSelect={(_, data) => handleFolderSelect(data.optionValue as string)}
                    style={{ flexGrow: 1 }}
                  >
                    {folders.map(function (folder) {
                      return (
                        <Option key={folder.id} value={folder.id}>
                          {folder.name}
                        </Option>
                      );
                    })}
                  </Dropdown>
                  <Button
                    icon={<ArrowClockwise24Regular />}
                    onClick={refreshFolders}
                    appearance="subtle"
                    aria-label="Refresh folders"
                  />
                </div>
              </div>

              <SignersList signers={signers} onChange={setSigners} />

              <SigningOptions
                outputFormat={outputFormat}
                onOutputFormatChange={setOutputFormat}
                sendImmediately={sendImmediately}
                onSendImmediatelyChange={setSendImmediately}
              />

              <div className={styles.buttonRow}>
                {showDebugButton && (
                  <DebugDialog
                    debugInfo={debugInfo}
                    isOpen={isDebugDialogOpen}
                    onOpenChange={setIsDebugDialogOpen}
                    onClear={() => setDebugInfo("")}
                    onCopy={copyDebugInfo}
                  />
                )}
                <Button
                  appearance="primary"
                  onClick={createContainer}
                  disabled={isCreatingContainer || !selectedFolder || !hasValidSigners}
                >
                  {isCreatingContainer ? (
                    <>
                      <Spinner size="tiny" />
                      &nbsp;Sending...
                    </>
                  ) : (
                    "Send for signing"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
      <Toaster />
    </FluentProvider>
  );
};

export default App;
